import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Label } from "@/components/ui/form/Label";

const textfieldVariants = cva(
  "border w-full rounded-full px-5 py-2.5 text-base font-weight-regular transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-2",
  {
    variants: {
      color: {
        purple: "border-purple bg-transparent text-black focus:outline-purple",
        yellow: "border-yellow bg-transparent text-black focus:outline-yellow",
        dark: "bg-black/40 backdrop-blur-md text-white border-white/60 placeholder:text-white/70 focus:outline-yellow",
      },
    },
    defaultVariants: {
      color: "purple",
    },
  }
);

export interface TextfieldProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, "color">,
    VariantProps<typeof textfieldVariants> {
  /** Error message displayed below the input */
  error?: string;
  /** Label for the input */
  label?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Textfield component for single-line text input.
 * Supports color variants, labels, and error states.
 */
export const Textfield: React.FC<TextfieldProps> = ({
  color = "purple",
  error,
  label,
  className,
  id,
  ...props
}) => {
  const inputId = id || props.name;

  return (
    <div className="flex flex-col gap-xs w-full">
      {label && (
        <Label
          htmlFor={inputId}
          color={color || "purple"}
          disabled={props.disabled}
          className="mx-5"
        >
          {label}
        </Label>
      )}
      <input
        type="text"
        id={inputId}
        className={textfieldVariants({
          color,
          className: `${error ? "border-red-500! focus:outline-red-500!" : ""} ${className}`,
        })}
        aria-invalid={!!error}
        aria-describedby={error && inputId ? `${inputId}-error` : undefined}
        aria-label={label || props["aria-label"] || props["placeholder"]}
        {...props}
      />

      {error && (
        <span
          id={inputId ? `${inputId}-error` : undefined}
          className="text-red-500 text-sm ml-4"
          role="alert"
        >
          {error}
        </span>
      )}
    </div>
  );
};
